// JavaScript
// src/firebase.js
import { initializeApp } from 'firebase/app';
import {
  getFirestore,
  deleteDoc,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
} from 'firebase/firestore';
import {
  GoogleAuthProvider,
  getAuth,
  signOut,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  checkActionCode,
  applyActionCode,
  updateProfile,
  updateEmail,
  deleteUser,
  fetchSignInMethodsForEmail,
} from 'firebase/auth';
import {
  getFunctions,
  // connectFunctionsEmulator,
  httpsCallable,
} from 'firebase/functions';
import { getStorage } from 'firebase/storage';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';
import * as Sentry from '@sentry/react';

import mapFirebaseAuthError, {
  captureUnmappedAuthError,
} from './mapFirebaseAuthError';
import { applyNeedsPermissionsRefresh } from './applyNeedsPermissionsRefresh';
import {
  buildEmailPasswordRegistrationDocument,
  upsertGoogleUserDocument,
  syncUserDocumentAfterSignIn,
  USERS_COLLECTION,
} from '../services/firestoreUserDocument';

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: import.meta.env.VITE_API_KEY,
  authDomain: import.meta.env.VITE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_DATABASE_URL,
  projectId: import.meta.env.VITE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_APP_ID,
  measurementId: import.meta.env.VITE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const googleProvider = new GoogleAuthProvider();
// Add scopes and custom parameters for better authentication flow
googleProvider.addScope('email');
googleProvider.addScope('profile');
googleProvider.setCustomParameters({
  prompt: 'select_account',
});

const auth = getAuth();
const functions = getFunctions();
const storage = getStorage(app);
const currentURL = window.location.href;

//for debnugging on localhost
if (currentURL.includes('localhost')) {
  // connectFirestoreEmulator(db, 'localhost', 8080);
  // connectFunctionsEmulator(functions, 'localhost', 5001);
  window.self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}
// Create a ReCaptchaEnterpriseProvider instance using your reCAPTCHA Enterprise
// site key and pass it to initializeAppCheck().
const appCheck = initializeAppCheck(app, {
  provider: new ReCaptchaEnterpriseProvider(
    import.meta.env.VITE_RECAPTCHA_SITE_KEY,
  ),
  isTokenAutoRefreshEnabled: true, // Set to true to allow auto-refresh.
});

/**
 * ====================
 * CLOUD FUNCTIONS
 * ====================
 */
/**
 * userGrantGlobalAdmin
 * callable cloud function to set globalAmin flag on a user
 */
// const userGrantGlobalAdmin = httpsCallable(
//   functions,
//   'callableUserGrantGlobalAdmin',
// );
const listUsers = httpsCallable(functions, 'callableUserListUsersV2');
const ittApi = httpsCallable(functions, 'ittApi');

/**
 * Auth mail goes through one callable. Callers pass the operation and payload.
 * The Functions SDK attaches App Check. Region stays the default us-central1.
 *
 * @param {string} operation
 * @param {unknown} payload
 * @returns {Promise<import('firebase/functions').HttpsCallableResult>}
 */
const callIttApi = (operation, payload) => ittApi({ operation, payload });

export {
  CALLABLE_BEER_ADD_BEER,
} from './callableBeerConstants';

/**
 * ===================
 * FUNCTIONS
 * ===================
 */
/**
 * OAuth users can have `user.email === null` while the address exists on provider data
 * or in the Auth console (e.g. with “multiple accounts per email”). Identity Toolkit’s
 * verify-email action requires a primary email; sync with `updateEmail` when possible.
 *
 * If that email is already the **primary** on another Auth user (often a separate
 * email/password account), `updateEmail` returns `auth/email-already-in-use`.
 * The client cannot attach that address to this user without account linking or Admin
 * tooling — surface a dedicated message instead of the registration copy.
 */
function throwVerificationEmailClaimConflict() {
  const conflict = new Error(
    'Cannot send verification: this email is used by another sign-in method.',
  );
  conflict.code = 'app/verification-email-claim-conflict';
  throw conflict;
}

async function ensurePrimaryEmailForVerification(firebaseUser) {
  await firebaseUser.reload();
  if (firebaseUser.email) {
    return;
  }

  let candidateEmail =
    firebaseUser.providerData?.find((entry) => entry.email)?.email?.trim() ||
    null;

  if (!candidateEmail && firebaseUser.uid) {
    try {
      const userRef = doc(db, USERS_COLLECTION, firebaseUser.uid);
      const snapshot = await getDoc(userRef);
      candidateEmail = snapshot.data()?.email?.trim() || null;
    } catch (readErr) {
      console.error('[firebase] ensurePrimaryEmail read users doc:', readErr);
      Sentry.captureException(readErr);
    }
  }

  if (!candidateEmail) {
    const missingEmail = new Error('No email available to verify.');
    missingEmail.code = 'auth/missing-email';
    throw missingEmail;
  }

  const linkedProviders = firebaseUser.providerData.map(
    (providerInfo) => providerInfo.providerId,
  );
  const userHasPasswordLinked = linkedProviders.includes('password');

  let signInMethodsForEmail = [];
  try {
    signInMethodsForEmail = await fetchSignInMethodsForEmail(
      auth,
      candidateEmail,
    );
  } catch (fetchErr) {
    console.warn('[firebase] fetchSignInMethodsForEmail skipped:', fetchErr);
  }

  if (
    !userHasPasswordLinked &&
    Array.isArray(signInMethodsForEmail) &&
    signInMethodsForEmail.includes('password')
  ) {
    throwVerificationEmailClaimConflict();
  }

  try {
    await updateEmail(firebaseUser, candidateEmail);
  } catch (updateErr) {
    if (updateErr?.code === 'auth/email-already-in-use') {
      throwVerificationEmailClaimConflict();
    }
    throw updateErr;
  }
  await firebaseUser.reload();
}

/**
 * Google signin
 */
const signInWithGoogle = async () => {
  try {
    const credential = await signInWithPopup(auth, googleProvider);
    await upsertGoogleUserDocument(db, credential);
    return credential;
  } catch (error) {
    console.error('Error initiating Google sign-in:', error);
    captureUnmappedAuthError(error);
    throw error;
  }
};

/**
 * Test popup signin for debugging
 */
const signInWithGooglePopup = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    await upsertGoogleUserDocument(db, result);
    return result;
  } catch (error) {
    console.error('Error with popup sign-in:', error);
    captureUnmappedAuthError(error);
    throw error;
  }
};

/**
 * Logout
 */
const logout = () => {
  signOut(auth);
};

/**
 * Sign in with email + password (Firebase Email/Password provider).
 */
const signInWithEmailPasswordFlow = async (email, password) => {
  const credential = await signInWithEmailAndPassword(auth, email, password);
  return credential;
};

/**
 * Password reset through ittApi `authSendPasswordReset`.
 * The server builds the link. Success is `data.sent === true` only.
 * Do not pass continue URLs, action-code settings, or the address in logs.
 */
const sendPasswordResetForEmail = async (emailAddress) => {
  const result = await callIttApi('authSendPasswordReset', {
    email: emailAddress,
  });
  if (result?.data?.sent !== true) {
    const failedSend = new Error('Password reset could not be sent.');
    failedSend.code = 'app/password-reset-send-failed';
    throw failedSend;
  }
};

const VERIFICATION_EMAIL_SEND_FAILED = 'app/verification-email-send-failed';
const VERIFICATION_EMAIL_SEND_FAILED_MESSAGE = 'Email could not be sent';

function httpsErrorCode(error) {
  const rawCode = typeof error?.code === 'string' ? error.code : '';
  if (rawCode.startsWith('functions/')) {
    return rawCode.slice('functions/'.length);
  }
  return rawCode;
}

/**
 * Map confirmation-mail failures onto the existing failed-send and
 * too-many-requests states. Do not surface the callable message.
 */
function mapAuthEmailConfirmationFailure(error) {
  const code = httpsErrorCode(error);
  if (code === 'resource-exhausted') {
    const blocked = new Error('Too many attempts. Please wait and try again.');
    blocked.code = 'auth/too-many-requests';
    return blocked;
  }
  if (
    code === 'unavailable'
    || code === 'unauthenticated'
    || code === 'failed-precondition'
  ) {
    const failedSend = new Error(VERIFICATION_EMAIL_SEND_FAILED_MESSAGE);
    failedSend.code = VERIFICATION_EMAIL_SEND_FAILED;
    return failedSend;
  }
  return error;
}

function throwNoSignedInUserForVerify() {
  const noUser = new Error('No signed-in user to send verification email.');
  noUser.code = 'app/no-signed-in-user-for-verify';
  throw noUser;
}

/**
 * Send (or resend) confirmation mail through ittApi `authSendEmailConfirmation`.
 * The payload is empty: the callable mails the signed-in Auth user's address.
 * Do not fall back to `sendEmailVerification`, with or without settings.
 */
const sendVerificationEmailToUser = async (firebaseUser) => {
  if (!firebaseUser) {
    throwNoSignedInUserForVerify();
  }
  await ensurePrimaryEmailForVerification(firebaseUser);

  const activeUser = auth.currentUser;
  if (!activeUser || activeUser.uid !== firebaseUser.uid) {
    throwNoSignedInUserForVerify();
  }
  if (!activeUser.email) {
    const missing = new Error('No email available to verify.');
    missing.code = 'auth/missing-email';
    throw missing;
  }

  try {
    await callIttApi('authSendEmailConfirmation', {});
  } catch (sendError) {
    throw mapAuthEmailConfirmationFailure(sendError);
  }
};

const checkAuthActionCode = (oobCode) => checkActionCode(auth, oobCode);

const applyAuthActionCode = (oobCode) => applyActionCode(auth, oobCode);

const reloadCurrentAuthUser = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    return null;
  }
  await currentUser.reload();
  return auth.currentUser;
};

/**
 * Merge Auth’s email onto `users/{uid}` only for the account signed in here,
 * and only after that account’s Auth email has changed.
 */
const mergeConfirmedAuthEmail = async (uid, email) => {
  if (!uid || !email || auth.currentUser?.uid !== uid) {
    return;
  }
  await setDoc(
    doc(db, USERS_COLLECTION, uid),
    {
      email,
      updatedAt: new Date().toISOString(),
    },
    { merge: true },
  );
};

/**
 * Create email/password account, bootstrap `users/{uid}`, send verification email.
 * Rolls back Auth user if Firestore bootstrap fails.
 *
 * @returns {Promise<{ credential: import('firebase/auth').UserCredential, verificationEmailError: string | null }>}
 */
const registerEmailPasswordUser = async ({
  email: emailInput,
  password,
  firstName: firstNameInput,
  lastName: lastNameInput,
  breweryRole: breweryRoleInput,
}) => {
  const firstName = firstNameInput.trim();
  const lastName = lastNameInput.trim();
  const emailNormalized = emailInput.trim();
  const breweryRole = breweryRoleInput.trim();
  const displayNameForAuth = `${firstName} ${lastName}`;

  if (!firstName) {
    const validationError = new Error('Please enter your first name.');
    validationError.code = 'app/missing-first-name';
    throw validationError;
  }
  if (!lastName) {
    const validationErrorLast = new Error('Please enter your last name.');
    validationErrorLast.code = 'app/missing-last-name';
    throw validationErrorLast;
  }

  let createdUser = null;
  let usersFirestoreDocCreated = false;
  let credential = null;

  try {
    credential = await createUserWithEmailAndPassword(
      auth,
      emailNormalized,
      password,
    );
    createdUser = credential.user;
    await updateProfile(createdUser, { displayName: displayNameForAuth });

    const registrationPayload = buildEmailPasswordRegistrationDocument({
      uid: createdUser.uid,
      emailNormalized,
      firstName,
      lastName,
      breweryRole,
    });

    await setDoc(
      doc(db, USERS_COLLECTION, createdUser.uid),
      registrationPayload,
      { merge: true },
    );
    usersFirestoreDocCreated = true;
  } catch (err) {
    if (createdUser) {
      if (usersFirestoreDocCreated) {
        try {
          await deleteDoc(doc(db, USERS_COLLECTION, createdUser.uid));
        } catch (deleteDocErr) {
          console.error(
            'registerEmailPasswordUser rollback deleteDoc(users):',
            deleteDocErr,
          );
          Sentry.captureException(deleteDocErr);
        }
      }
      try {
        await deleteUser(createdUser);
      } catch (deleteErr) {
        console.error('registerEmailPasswordUser rollback deleteUser:', deleteErr);
        Sentry.captureException(deleteErr);
      }
    }
    console.error('registerEmailPasswordUser:', err);
    Sentry.captureException(err);
    throw err;
  }

  let verificationEmailError = null;
  try {
    await sendVerificationEmailToUser(createdUser);
  } catch (emailErr) {
    console.error('registerEmailPasswordUser sendVerificationEmailToUser:', emailErr);
    Sentry.captureException(emailErr);
    verificationEmailError = mapFirebaseAuthError(emailErr);
  }

  return { credential, verificationEmailError };
};

/**
 * Reads custom claims used for Engage session state (forces token refresh).
 * @returns {Promise<{ isGlobalAdmin: boolean, isBreweryAdmin: boolean }>}
 */
async function getSessionClaims(user) {
  const idTokenResult = await user.getIdTokenResult(true);
  return {
    isGlobalAdmin: Boolean(idTokenResult.claims.globalAdmin),
    isBreweryAdmin: Boolean(idTokenResult.claims.isBreweryAdmin),
  };
}

/**
 * function that checks if the current user has the globalAdmin custom scope
 */
const isGlobalAdmin = async (user) => {
  if (!user) {
    return null;
  }

  try {
    const sessionClaims = await getSessionClaims(user);
    return sessionClaims.isGlobalAdmin;
  } catch (error) {
    console.error('Error getting ID token result:', error);
    Sentry.captureException(error);
    throw error;
  }
};

/**
 * Build the same UserContext payload as `onAuthStateChanged`, for a given Firebase user
 * (or signed-out when `firebaseUser` is null). Resolves when admin check finishes.
 */
function publishUserSession(firebaseUser, callback) {
  if (!firebaseUser) {
    const signedOut = {
      isGlobalAdmin: false,
      isBreweryAdmin: false,
      isLoggedIn: false,
      isLoading: false,
      uid: null,
      emailVerified: null,
      userName: null,
      userEmail: null,
    };
    callback(signedOut); // skipcq: JS-0255
    return Promise.resolve(signedOut);
  }

  const userName = firebaseUser.displayName;
  const emailVerified = firebaseUser.emailVerified;
  const userEmail =
    firebaseUser.email ||
    firebaseUser.providerData?.find((entry) => entry.email)?.email ||
    null;

  const syncPromise = syncUserDocumentAfterSignIn(db, firebaseUser).catch((syncErr) => {
    console.error('syncUserDocumentAfterSignIn:', syncErr);
    Sentry.captureException(syncErr);
  });

  const claimsPromise = getSessionClaims(firebaseUser)
    .then((sessionClaims) => ({
      sessionClaims,
      claimsError: null,
    }))
    .catch((error) => {
      console.error('Error checking auth custom claims:', error);
      return {
        sessionClaims: { isGlobalAdmin: false, isBreweryAdmin: false },
        claimsError: error,
      };
    });

  return Promise.all([claimsPromise, syncPromise]).then(([claimsOutcome]) => {
    const state = {
      isGlobalAdmin: claimsOutcome.sessionClaims.isGlobalAdmin,
      isBreweryAdmin: claimsOutcome.sessionClaims.isBreweryAdmin,
      isLoggedIn: true,
      uid: firebaseUser.uid,
      userName,
      userEmail,
      emailVerified,
      isLoading: false,
      ...(claimsOutcome.claimsError ? { error: claimsOutcome.claimsError } : {}),
    };
    // skipcq: JS-0255
    callback(state);
    return state;
  });
}

/**
 * Re-run session resolution for `auth.currentUser` (e.g. after `user.reload()` updates
 * `emailVerified`) so React context matches Firebase before route guards run.
 */
function refreshAuthSessionState(setUser) {
  return publishUserSession(auth.currentUser, setUser);
}

// register a listener for authentication state changes
// see https://johnwcassidy.medium.com/firebase-authentication-hooks-and-context-d0e47395f402
function onAuthStateChange(callback) {
  let userDocUnsubscribe = null;
  let permissionsRefreshInflight = false;

  const authUnsubscribe = auth.onAuthStateChanged((firebaseUser) => {
    if (userDocUnsubscribe) {
      userDocUnsubscribe();
      userDocUnsubscribe = null;
    }

    publishUserSession(firebaseUser, callback);

    if (!firebaseUser) {
      return;
    }

    const userRef = doc(db, USERS_COLLECTION, firebaseUser.uid);
    userDocUnsubscribe = onSnapshot(userRef, async (snapshot) => {
      const needsRefresh = snapshot.data()?.needsPermissionsRefresh === true;
      if (!needsRefresh || permissionsRefreshInflight) {
        return;
      }
      permissionsRefreshInflight = true;
      try {
        await applyNeedsPermissionsRefresh({
          firebaseUser,
          userRef,
          updateDoc,
          publishUserSession,
          sessionCallback: callback,
        });
      } catch (refreshErr) {
        console.error('needsPermissionsRefresh handling failed:', refreshErr);
        Sentry.captureException(refreshErr);
      } finally {
        permissionsRefreshInflight = false;
      }
    });
  });

  return () => {
    if (userDocUnsubscribe) {
      userDocUnsubscribe();
    }
    authUnsubscribe();
  };
}

export {
  auth,
  db,
  appCheck,
  // grantGlobalAdmin,
  isGlobalAdmin,
  listUsers,
  logout,
  registerEmailPasswordUser,
  callIttApi,
  sendPasswordResetForEmail,
  sendVerificationEmailToUser,
  checkAuthActionCode,
  applyAuthActionCode,
  reloadCurrentAuthUser,
  mergeConfirmedAuthEmail,
  signInWithEmailPasswordFlow,
  signInWithGoogle,
  signInWithGooglePopup,
  storage,
  onAuthStateChange,
  refreshAuthSessionState,
  firebaseConfig,
  functions,
};
